import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import API_URL from '../lib/apiUrl';

/**
 * Custom hook for auto-saving and draft management
 * @param {string} draftType - Type of draft (organization, core_assessment, etc.)
 * @param {string|null} orgId - Organization ID (null for new)
 * @param {object} formData - Current form data
 * @param {function} setFormData - Function to update form data
 * @param {string} token - Auth token
 * @param {number} autoSaveInterval - Auto-save interval in milliseconds (default: 60000)
 */
export const useAutoSave = (draftType, orgId, formData, setFormData, token, autoSaveInterval = 60000, enabled = true, loadExistingDraft = true) => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const previousDataRef = useRef(null);
  const loadedDraftKeyRef = useRef(null);

  const hasMeaningfulData = useCallback((data) => Object.values(data || {}).some((value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === 'object') return Object.keys(value).length > 0;
    return value !== '' && value !== null && value !== undefined && value !== 0 && value !== false;
  }), []);

  // Load draft on mount
  const loadDraft = useCallback(async () => {
    if (!token || !enabled || !loadExistingDraft) return;
    const draftKey = `${draftType}:${orgId || 'new'}`;
    if (loadedDraftKeyRef.current === draftKey) return;
    setDraftLoaded(false);
    
    try {
      const params = orgId ? `?org_id=${orgId}` : '';
      const response = await axios.get(`${API_URL}/api/drafts/${draftType}${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.draft?.data) {
        setFormData(prev => ({ ...prev, ...response.data.draft.data }));
        setHasDraft(true);
        setLastSaved(new Date(response.data.draft.updated_at));
        previousDataRef.current = JSON.stringify(response.data.draft.data);
      } else {
        previousDataRef.current = JSON.stringify(formData);
      }
      loadedDraftKeyRef.current = draftKey;
      setDraftLoaded(true);
    } catch (error) {
      console.log('No existing draft found');
      previousDataRef.current = JSON.stringify(formData);
      loadedDraftKeyRef.current = draftKey;
      setDraftLoaded(true);
    }
  }, [draftType, orgId, token, setFormData, enabled, formData, loadExistingDraft]);

  // Save draft function
  const saveDraft = useCallback(async (silent = true) => {
    if (!token || !formData || !enabled || !draftLoaded || !hasMeaningfulData(formData)) return false;
    
    // Check if data actually changed (skip for manual saves to always confirm save)
    const currentData = JSON.stringify(formData);
    if (silent && currentData === previousDataRef.current) {
      return false;
    }
    
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      await axios.post(`${API_URL}/api/drafts/save`, {
        draft_type: draftType,
        org_id: orgId,
        data: formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      previousDataRef.current = currentData;
      setLastSaved(new Date());
      setHasDraft(true);
      setSaveSuccess(true);
      
      if (!silent) {
        toast.success('Draft saved successfully', {
          style: { background: '#10B981', color: 'white' }
        });
      }
      
      // Reset success indicator after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
      
      return true;
    } catch (error) {
      console.error('Draft save error:', error);
      // Don't show error toast - silently fail
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [draftType, orgId, formData, token, enabled, draftLoaded, hasMeaningfulData]);

  // Delete draft function (call after successful submission)
  const deleteDraft = useCallback(async () => {
    if (!token || !enabled) return;
    
    try {
      const params = orgId ? `?org_id=${orgId}` : '';
      await axios.delete(`${API_URL}/api/drafts/${draftType}${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasDraft(false);
      previousDataRef.current = null;
    } catch (error) {
      console.error('Failed to delete draft:', error);
    }
  }, [draftType, orgId, token, enabled]);

  // Load draft on mount
  useEffect(() => {
    if (loadExistingDraft) {
      loadDraft();
      return;
    }
    if (!enabled) return;
    const draftKey = `${draftType}:${orgId || 'new'}`;
    if (loadedDraftKeyRef.current !== draftKey) {
      previousDataRef.current = JSON.stringify(formData);
      loadedDraftKeyRef.current = draftKey;
      setDraftLoaded(true);
    }
  }, [loadDraft, loadExistingDraft, enabled, draftType, orgId, formData]);

  // Debounce saves after edits so closing the page does not lose recent answers.
  useEffect(() => {
    if (!token || !enabled || !draftLoaded || !hasMeaningfulData(formData)) return undefined;
    const timer = setTimeout(() => saveDraft(true), 1500);
    return () => clearTimeout(timer);
  }, [formData, token, enabled, draftLoaded, saveDraft, hasMeaningfulData]);

  // Auto-save effect (silent, no messages)
  useEffect(() => {
    if (!token || !enabled || !draftLoaded) return;
    
    const interval = setInterval(() => {
      saveDraft(true); // Silent save
    }, autoSaveInterval);
    
    return () => clearInterval(interval);
  }, [saveDraft, autoSaveInterval, token, enabled, draftLoaded]);

  // Save on page visibility change (when user switches tabs or minimizes)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && token && enabled) {
        saveDraft(true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [saveDraft, token, enabled]);

  // Keep the final request alive while navigating away or closing the tab.
  useEffect(() => {
    const saveOnExit = () => {
      const currentData = JSON.stringify(formData);
      if (currentData !== previousDataRef.current && token && enabled && hasMeaningfulData(formData)) {
        const data = JSON.stringify({
          draft_type: draftType,
          org_id: orgId,
          data: formData
        });
        fetch(`${API_URL}/api/drafts/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: data,
          keepalive: true
        }).catch(() => undefined);
      }
    };

    window.addEventListener('pagehide', saveOnExit);
    return () => window.removeEventListener('pagehide', saveOnExit);
  }, [formData, draftType, orgId, token, enabled, hasMeaningfulData]);

  return {
    isSaving,
    lastSaved,
    hasDraft,
    draftLoaded,
    saveSuccess,
    saveDraft: () => saveDraft(false), // Manual save (non-silent)
    deleteDraft,
    loadDraft
  };
};

export default useAutoSave;
